import { createContext, useCallback, useContext, useEffect, useRef } from "react";
import { useBlocker } from "react-router-dom";
import { useConfirm } from "./components/confirm-dialog";

const NavigationGuardContext = createContext(null);

// NavigationGuardProvider registers the only blocker of the router, as react-router supports one blocker at a time.
// Pages add guards with useConfirmBeforeNavigate, and navigation is confirmed when any of the guards has unsaved changes.
export const NavigationGuardProvider = ({ children }) => {
    const confirm = useConfirm();
    const guardsRef = useRef(new Set());

    // the identity is stable, so the blocker is registered once
    const shouldBlock = useCallback(() => {
        for (const guard of guardsRef.current) {
            if (guard.current()) return true;
        }
        return false;
    }, []);
    const blocker = useBlocker(shouldBlock);
    // the latest blocker and confirm are read from refs, so the confirmation is asked once per blocked navigation
    const blockerRef = useRef(blocker);
    blockerRef.current = blocker;
    const confirmRef = useRef(confirm);
    confirmRef.current = confirm;

    useEffect(() => {
        if (blocker.state !== "blocked") return;
        let cancelled = false;
        (async () => {
            const isConfirmed = await confirmRef.current("You have unsaved changes. Leave without saving?");
            if (cancelled) return;
            if (isConfirmed) {
                blockerRef.current.proceed();
            } else {
                blockerRef.current.reset();
            }
        })();
        return () => { cancelled = true };
    }, [blocker.state]);

    const addGuard = useCallback((guard) => {
        guardsRef.current.add(guard);
        return () => guardsRef.current.delete(guard);
    }, []);

    return (
        <NavigationGuardContext.Provider value={addGuard}>
            {children}
        </NavigationGuardContext.Provider>
    );
};

// useConfirmBeforeNavigate asks for confirmation before navigating away with unsaved changes.
// Set skipRef.current to navigate without confirmation, e.g. after saving.
export function useConfirmBeforeNavigate(hasUnsavedChanges, skipRef) {
    const addGuard = useContext(NavigationGuardContext);
    // the guard reads the latest values, so it is added once
    const guard = useRef(null);
    guard.current = () => !skipRef?.current && hasUnsavedChanges;

    useEffect(() => addGuard(guard), [addGuard]);
}
